// ─────────────────────────────────────────────────────────────────────────────
// audio_channel.ts — Named audio bus
//
// A `Channel` is a thin wrapper around an input/output GainNode pair:
// instruments (oscillator-based or sample-based) connect to `input`, the
// mixer pulls from `output`. The class is intentionally agnostic about
// what feeds it — instruments, textures, samples, or anything else can
// be plugged in or swapped without touching the channel itself.
//
// One optional pre-output slot (a single AudioNode) can be inserted as a
// per-channel insert effect. It's wired in lazily and bypassed by
// default, so callers that don't need it pay nothing.
// ─────────────────────────────────────────────────────────────────────────────

export class AudioChannel {
  /** Modules connect their output here. */
  readonly input: GainNode;
  /** The mixer pulls from this. */
  readonly output: GainNode;

  private _insert: AudioNode | null = null;

  constructor(
    private readonly _ctx: AudioContext,
    public readonly id: string,
  ) {
    this.input = _ctx.createGain();
    this.output = _ctx.createGain();
    // Default routing: input → output (no insert).
    this.input.connect(this.output);
  }

  /** Channel volume (multiplier). Default 1. */
  setVolume(v: number): void {
    this.output.gain.setTargetAtTime(v, this._ctx.currentTime, 0.005);
  }
  get volume(): number { return this.output.gain.value; }

  /** Insert a per-channel processor between input and output. Pass `null`
   *  to remove the insert. Internal routing is updated atomically. */
  setInsert(node: AudioNode | null): void {
    // Disconnect current routing
    try { this.input.disconnect(); } catch { /* may have been disconnected */ }
    if (this._insert) {
      try { this._insert.disconnect(); } catch { /* idem */ }
    }
    this._insert = node;
    if (node) {
      this.input.connect(node);
      node.connect(this.output);
    } else {
      this.input.connect(this.output);
    }
  }
}
